import React, { useState, useMemo, useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
  Divider,
} from '@mui/material'
import {
  Search as SearchIcon,
  ViewList as ViewListIcon,
  GridView as GridViewIcon,
  KeyboardDoubleArrowLeft as FirstIcon,
  KeyboardDoubleArrowRight as LastIcon,
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon,
  Logout as LogoutIcon,
  OpenInNewRounded as OpenInNewIcon,
} from '@mui/icons-material'

import Logo from '@/assets/AppLogo.png'
import ThemeToggle from '@/components/sidebar/ThemeToggle'
import { useAppTheme } from '@/theme/ThemeContext'
import { useAuth } from '@/auth/AuthContext'
import { useSitesStore } from '@/store/sitesStore'
import { getSites, type Site } from '@/services/sitesService'

const PAGE_SIZE = 15

// ─── Theme tokens ─────────────────────────────────────────────────────────────

const tok = (dark: boolean) => ({
  pageBg: dark ? '#192336' : '#F5F7FA',
  cardBg: dark ? '#091327' : '#FFFFFF',
  cardBorder: dark ? '#43597F' : 'rgba(13,63,97,0.08)',
  cardShadow: dark ? '0 4px 20px rgba(0,0,0,0.2)' : '0 2px 8px rgba(0,0,0,0.04)',
  title: dark ? '#BBD7FF' : '#0B4F6C',
  textPrimary: dark ? '#BBD7FF' : '#0B2D52',
  textSecondary: dark ? '#BBD7FF' : '#64748B',
  accentBlue: dark ? '#003089' : '#2B7BCE',
  accentIcon: dark ? '#FFFFFF' : '#FFFFFF',
  chipBg: dark ? 'rgba(0, 48, 137, 0.25)' : '#f0f4f9',
  chipIcon: dark ? '#63A4FF' : '#2B7BCE',
  activeBg: dark ? '#1e293b' : '#FFFFFF',
  activeFg: dark ? '#38bdf8' : '#1D3981',
  mutedFg: dark ? '#64748b' : '#94A3B8',
  logout: '#F87171',
  divider: dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
  searchBg: dark ? '#0F172A' : '#FFFFFF',
  searchBorder: dark ? 'rgba(255,255,255,0.1)' : '#e0e6ed',
})

type Tok = ReturnType<typeof tok>

// ─── View toggle button ───────────────────────────────────────────────────────

const ViewBtn = ({
  active, onClick, children, t,
}: { active: boolean; onClick: () => void; children: React.ReactNode; t: Tok }) => (
  <IconButton
    onClick={onClick}
    size="small"
    disableRipple={false}
    sx={{
      width: 40, height: 40,
      borderRadius: '8px',
      bgcolor: active ? t.accentBlue : t.searchBg,
      color: active ? '#FFFFFF' : t.textSecondary,
      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      border: `1px solid ${active ? t.accentBlue : t.divider}`,
      '&:hover': { bgcolor: active ? t.accentBlue : t.activeBg, opacity: 0.9 },
    }}
  >
    {children}
  </IconButton>
)

// ─── Site card (grid) ─────────────────────────────────────────────────────────

const SiteCard = ({ site, onSelect, t }: { site: Site; onSelect: (s: Site) => void; t: Tok }) => (
  <Box
    onClick={() => onSelect(site)}
    sx={{
      bgcolor: t.cardBg,
      border: `1px solid ${t.cardBorder}`,
      boxShadow: t.cardShadow,
      borderRadius: '12px',
      px: '24px', py: '20px',
      cursor: 'pointer',
      display: 'flex',
      direction: 'rtl',
      alignItems: 'center',
      justifyContent: 'space-between',
      height: '100px',
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        transform: 'translateY(-3px)',
        borderColor: 'rgba(56, 189, 248, 0.4)',
        boxShadow: '0 12px 30px rgba(0,0,0,0.3)',
      },
    }}
  >
    {/* Right side: Icon + Name/Desc */}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px', flex: 1 }}>
      <Box sx={{ overflow: 'hidden', textAlign: 'right' }}>
        <Typography sx={{
          fontWeight: 600, fontSize: 16, color: t.textPrimary,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {site.siteName}
        </Typography>
        <Typography sx={{ fontSize: 12, color: t.textSecondary, mt: '2px' }}>
          {site.siteName}
        </Typography>
      </Box>

      <Box sx={{
        width: 32, height: 32,
        borderRadius: '8px',
        bgcolor: t.accentBlue,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0
      }}>
        <OpenInNewIcon sx={{ fontSize: 18, color: t.accentIcon, transform: 'scaleX(-1)' }} />
      </Box>
    </Box>

    {/* Left side: ID */}
    <Box sx={{ textAlign: 'left', minWidth: '60px' }}>
      <Typography sx={{ fontWeight: 700, fontSize: 20, color: t.textPrimary, lineHeight: 1 }}>
        {site.armCount ?? 0}
      </Typography>
      <Typography sx={{ fontSize: 11, color: t.textSecondary, mt: '4px' }}>
        יח' קצה
      </Typography>
    </Box>
  </Box>
)

// ─── Site row (list view) ─────────────────────────────────────────────────────

const SiteRow = ({ site, onSelect, t }: { site: Site; onSelect: (s: Site) => void; t: Tok }) => {
  return (
    <Box
      onClick={() => onSelect(site)}
      sx={{
        bgcolor: t.cardBg,
        border: `1px solid ${t.cardBorder}`,
        borderRadius: '6px',
        px: '18px', py: '6px',
        cursor: 'pointer',
        display: 'flex',
        direction: 'rtl',
        alignItems: 'center',
        gap: '0px',
        transition: 'all 0.1s ease',
        '&:hover': { bgcolor: t.activeBg, borderColor: t.accentBlue },
        minHeight: '48px',
      }}
    >
      {/* 1. Name & Icon — RIGHT (flex: 1.5) */}
      <Box sx={{ flex: 1.5, display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'flex-start' }}>
        <Box sx={{ textAlign: 'right' }}>
          <Typography sx={{ fontWeight: 600, fontSize: 13, color: t.textPrimary }}>
            {site.siteName}
          </Typography>
        </Box>
        <Box sx={{
          width: 28, height: 28, borderRadius: '5px',
          bgcolor: t.accentBlue,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <OpenInNewIcon sx={{ fontSize: 14, color: '#FFFFFF', transform: 'scaleX(-1)' }} />
        </Box>
      </Box>

      {/* 2. Utility — MIDDLE (flex: 1) */}
      <Box sx={{ flex: 1, textAlign: 'center' }}>
        <Typography sx={{ fontSize: 12, color: t.textSecondary }}>
          מי תמר
        </Typography>
      </Box>

      <Box sx={{ flex: 1, textAlign: 'left' }}>
        <Typography sx={{ fontWeight: 600, fontSize: 14, color: t.textPrimary }}>
          {site.armCount ?? 0}
        </Typography>
      </Box>
    </Box>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const SiteSelection: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  // Set by BackToSitesButton: the user chose to come back, so a lone site must
  // not be auto-selected again (that would bounce them straight back).
  const cameBackOnPurpose = Boolean(
    (location.state as { fromSiteBack?: boolean } | null)?.fromSiteBack
  )
  const { mode, toggleTheme, langKey, toggleLanguage } = useAppTheme()
  const dark = mode === 'dark'
  const { accountName, logout } = useAuth()
  const { setSelectedSite } = useSitesStore()
  const t = tok(dark)

  const [view, setView] = useState<'grid' | 'list'>('grid')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)

  const { data: all = [], isLoading, isError, error } = useQuery({
    queryKey: ['sites'],
    queryFn: getSites,
  })


  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return q ? all.filter(s => s.siteName.toLowerCase().includes(q)) : all
  }, [all, search])

  const total = filtered.length
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const cur = Math.min(page, totalPages)
  const shown = filtered.slice((cur - 1) * PAGE_SIZE, cur * PAGE_SIZE)

  const go = (p: number) => setPage(Math.max(1, Math.min(totalPages, p)))
  const pick = (s: Site) => { setSelectedSite(s); navigate(`/${s.siteId}/home`, { replace: true }) }
  const onLogout = () => { logout(); navigate('/login', { replace: true }) }
  const onSearch = (v: string) => { setSearch(v); setPage(1) }

  // Auto-navigate only when there's exactly one site. With multiple sites the
  // user must pick (is_default just pre-highlights a card in the list).
  const autoNavigated = useRef(false)
  useEffect(() => {
    if (cameBackOnPurpose) return
    if (autoNavigated.current || isLoading || isError || all.length === 0) return
    if (all.length === 1) {
      autoNavigated.current = true
      pick(all[0])
    }
  }, [all, isLoading, isError])

  const first = total ? (cur - 1) * PAGE_SIZE + 1 : 0
  const last = Math.min(cur * PAGE_SIZE, total)

  return (
    <Box sx={{
      width: '100vw', height: '100vh',
      bgcolor: t.pageBg,
      display: 'flex', flexDirection: 'column',
      overflow: 'hidden',
      transition: 'background-color 0.3s',
    }}>

      {/* ══ HEADER ══════════════════════════════════════════════════════════ */}
      <Box sx={{ px: '40px', pt: '20px', pb: '14px' }}>

        {/* Row A — Logo + Title (Far Left) */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: '12px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Box
              component="img" src={Logo}
              sx={{ width: 44, height: 50, objectFit: 'cover', objectPosition: 'top', borderRadius: '50%' }}
            />
            <Typography sx={{ fontWeight: 700, fontSize: 20, color: t.title, letterSpacing: '0.01em' }}>
              Rimonim Tech SmartNet
            </Typography>
          </Box>
        </Box>

        {/* Row B — Logout (Left) & Welcome (Right) — Aligned with common padding (e.g. 180px offset) */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box
            onClick={onLogout}
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: '3px',
              cursor: 'pointer', userSelect: 'none',
              ml: { lg: '140px', md: '60px', xs: '0px' },
              '&:hover': { opacity: 0.75 },
            }}
          >
            <Typography sx={{ fontSize: 12, color: t.logout }}>התנתק</Typography>
            <LogoutIcon sx={{ fontSize: 16, color: t.logout, transform: 'scaleX(-1)' }} />
          </Box>

          <Typography sx={{
            fontWeight: 400, fontSize: 15, color: t.textPrimary, mb: '-4px',
            mr: { lg: '140px', md: '60px', xs: '0px' }
          }}>
            <Box component="span" sx={{ fontWeight: 700 }}>{accountName ?? ''}</Box>
            {' '}, ברוכים הבאים
          </Typography>
        </Box>
      </Box>

      {/* Aligned Line/Divider */}
      <Box sx={{ px: { xs: '20px', md: '100px', lg: '120px' } }}>
        <Divider sx={{ borderColor: t.divider }} />
      </Box>

      {/* ══ TOOLBAR ═════════════════════════════════════════════════════════ */}
      <Box sx={{ px: { xs: '20px', md: '100px', lg: '180px' }, py: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
        <ViewBtn active={view === 'grid'} onClick={() => setView('grid')} t={t}>
          <GridViewIcon sx={{ fontSize: 20 }} />
        </ViewBtn>

        <ViewBtn active={view === 'list'} onClick={() => setView('list')} t={t}>
          <ViewListIcon sx={{ fontSize: 20 }} />
        </ViewBtn>

        <TextField
          value={search}
          onChange={e => onSearch(e.target.value)}
          placeholder="חיפוש אתר"
          size="small"
          inputProps={{ dir: 'rtl' }}
          sx={{
            ml: '12px',
            width: 340,
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px',
              bgcolor: t.searchBg,
              height: '40px',
              fontSize: 14,
              '& fieldset': { borderColor: t.searchBorder },
              '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
              '&.Mui-focused fieldset': { borderColor: t.accentBlue, borderWidth: '1px' },
            },
            '& input': { color: t.textPrimary, padding: '0 12px' },
            '& input::placeholder': { color: t.textSecondary, opacity: 0.6 },
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start" sx={{ ml: '6px', mr: '2px' }}>
                  <SearchIcon sx={{ fontSize: 18, color: t.textSecondary }} />
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>

      {/* ══ CONTENT ═════════════════════════════════════════════════════════ */}
      <Box sx={{ flex: 1, overflow: 'auto', px: { xs: '20px', md: '100px', lg: '180px' }, pt: '18px', pb: '8px' }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: '60px' }}>
            <CircularProgress size={28} sx={{ color: t.chipIcon }} />
          </Box>

        ) : isError ? (
          <Box sx={{ pt: '60px', textAlign: 'center' }}>
            <Typography sx={{ color: '#E53935', fontSize: 14 }}>שגיאה בטעינת האתרים</Typography>
            <Typography sx={{ color: t.textSecondary, fontSize: 12, mt: '6px' }}>
              {(error as any)?.response?.status} – {(error as any)?.message}
            </Typography>
          </Box>

        ) : shown.length === 0 ? (
          <Box sx={{ pt: '60px', textAlign: 'center' }}>
            <Typography sx={{ color: t.textSecondary, fontSize: 14 }}>לא נמצאו אתרים</Typography>
          </Box>

        ) : view === 'grid' ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', direction: 'rtl' }}>
            {shown.map(s => <SiteCard key={s.siteId} site={s} onSelect={pick} t={t} />)}
          </Box>

        ) : (
          /* ── List ── */
          <Box sx={{ maxWidth: '1300px', mx: 'auto' }}>
            <Box sx={{
              display: 'flex', direction: 'rtl',
              px: '20px', pb: '10px',
              borderBottom: `1px solid ${t.divider}`,
              mb: '10px'
            }}>
              <Box sx={{ flex: 1.5, textAlign: 'right' }}>
                <Typography sx={{ fontSize: 15, fontWeight: 600, color: t.textSecondary }}>שם אתר</Typography>
              </Box>
              <Box sx={{ flex: 1, textAlign: 'center' }}>
                <Typography sx={{ fontSize: 15, fontWeight: 600, color: t.textSecondary }}>תאגיד מים</Typography>
              </Box>
              <Box sx={{ flex: 1, textAlign: 'left' }}>
                <Typography sx={{ fontSize: 15, fontWeight: 600, color: t.textSecondary }}>מס' יחידות קצה</Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', mt: '8px' }}>
              {shown.map(s => <SiteRow key={s.siteId} site={s} onSelect={pick} t={t} />)}
            </Box>
          </Box>
        )}
      </Box>

      {/* ══ PAGINATION (only when > PAGE_SIZE) ══════════════════════════════ */}
      {total > PAGE_SIZE && (
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: '2px', py: '10px',
        }}>
          <IconButton size="small" onClick={() => go(1)} disabled={cur === 1}
            sx={{ color: t.textSecondary }}>
            <FirstIcon sx={{ fontSize: 15 }} />
          </IconButton>
          <IconButton size="small" onClick={() => go(cur - 1)} disabled={cur === 1}
            sx={{ color: t.textSecondary }}>
            <PrevIcon sx={{ fontSize: 15 }} />
          </IconButton>
          <Typography sx={{ fontSize: 12, color: t.textPrimary, mx: '8px', direction: 'rtl', whiteSpace: 'nowrap' }}>
            עמוד {cur} של {totalPages}
          </Typography>
          <IconButton size="small" onClick={() => go(cur + 1)} disabled={cur === totalPages}
            sx={{ color: t.textSecondary }}>
            <NextIcon sx={{ fontSize: 15 }} />
          </IconButton>
          <IconButton size="small" onClick={() => go(totalPages)} disabled={cur === totalPages}
            sx={{ color: t.textSecondary }}>
            <LastIcon sx={{ fontSize: 15 }} />
          </IconButton>
          <Typography sx={{ fontSize: 11, color: t.textSecondary, mr: '10px', direction: 'rtl', whiteSpace: 'nowrap' }}>
            {first} עד {last} מתוך {total}
          </Typography>
        </Box>
      )}

      {/* ══ FOOTER ══════════════════════════════════════════════════════════ */}
      <Box sx={{
        px: '40px', pb: '16px',
        display: 'flex', flexDirection: 'column', gap: 1
      }}>
        <Box
          onClick={toggleLanguage}
          sx={{
            px: 1.2,
            borderRadius: 1,
            fontSize: 16,
            fontWeight: 400,
            color: dark ? '#FFFFFF' : '#003F61',
            textAlign: 'center',
            width: 'fit-content',
            minWidth: 36,
            fontFamily: 'Rubik, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif',
            transition: 'all 0.3s ease',
            cursor: 'pointer',
            '&:hover': {
              bgcolor: dark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
            }
          }}
        >
          {langKey.toUpperCase()}
        </Box>
        <ThemeToggle isDarkMode={dark} onThemeToggle={toggleTheme} />
      </Box>
    </Box>
  )
}

export default SiteSelection
